/**
 * PromotionDialog Component
 * 
 * Modal dialog for pawn promotion piece selection.
 * Displays when a pawn reaches the opposite end of the board.
 * 
 * Requirements: 6.2, 6.3, 6.4
 */

import React, { useEffect, useCallback, useRef } from 'react';
import { PieceType, PlayerColor } from '../types';
import './PromotionDialog.css';

export interface PromotionDialogProps {
  /** The color of the pawn being promoted */
  color: PlayerColor;
  /** Callback when a piece is selected */
  onSelect: (pieceType: 'queen' | 'rook' | 'bishop' | 'knight') => void;
  /** Whether the dialog is visible */
  isOpen: boolean;
}

const PROMOTION_PIECES: Array<{ type: 'queen' | 'rook' | 'bishop' | 'knight'; label: string }> = [
  { type: 'queen', label: 'Queen' },
  { type: 'rook', label: 'Rook' },
  { type: 'bishop', label: 'Bishop' },
  { type: 'knight', label: 'Knight' }
];

/**
 * PromotionDialog component for selecting promotion piece
 */
export const PromotionDialog: React.FC<PromotionDialogProps> = ({
  color,
  onSelect,
  isOpen
}) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Focus the first button when dialog opens
  useEffect(() => {
    if (isOpen && buttonRefs.current[0]) {
      buttonRefs.current[0].focus();
    }
  }, [isOpen]);

  /**
   * Handles keyboard navigation
   */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : PROMOTION_PIECES.length - 1));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setSelectedIndex(prev => (prev < PROMOTION_PIECES.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : PROMOTION_PIECES.length - 1));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < PROMOTION_PIECES.length - 1 ? prev + 1 : 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onSelect(PROMOTION_PIECES[selectedIndex].type);
        break;
      case 'Escape':
        e.preventDefault();
        // Default to queen on escape
        onSelect('queen');
        break;
      case '1':
        e.preventDefault();
        onSelect('queen');
        break;
      case '2':
        e.preventDefault();
        onSelect('rook');
        break;
      case '3':
        e.preventDefault();
        onSelect('bishop');
        break;
      case '4':
        e.preventDefault();
        onSelect('knight');
        break;
    }
  }, [isOpen, selectedIndex, onSelect]);

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, handleKeyDown]);

  // Focus the selected button when selection changes
  useEffect(() => {
    if (isOpen && buttonRefs.current[selectedIndex]) {
      buttonRefs.current[selectedIndex]?.focus();
    }
  }, [selectedIndex, isOpen]);

  /**
   * Renders a chess piece using Unicode symbols
   */
  const renderPiece = (pieceType: 'queen' | 'rook' | 'bishop' | 'knight'): string => {
    const pieceSymbols: Record<string, Record<string, string>> = {
      white: {
        queen: '♕',
        rook: '♖',
        bishop: '♗',
        knight: '♘'
      },
      black: {
        queen: '♛',
        rook: '♜',
        bishop: '♝',
        knight: '♞'
      }
    };

    return pieceSymbols[color][pieceType] || '';
  };

  /**
   * Handles piece selection
   */
  const handleSelect = useCallback((pieceType: 'queen' | 'rook' | 'bishop' | 'knight') => {
    // Add haptic feedback if available
    if ('vibrate' in navigator) {
      navigator.vibrate(15);
    }
    onSelect(pieceType);
  }, [onSelect]);

  if (!isOpen) return null;

  return (
    <div 
      className="promotion-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="promotion-dialog-title"
    >
      <div 
        className="promotion-dialog"
        ref={dialogRef}
      >
        <h2 
          id="promotion-dialog-title" 
          className="promotion-dialog__title"
        >
          Choose Promotion Piece
        </h2>
        
        <p className="promotion-dialog__subtitle">
          Select which piece your pawn should become
        </p>

        <div className="promotion-dialog__pieces">
          {PROMOTION_PIECES.map((piece, index) => (
            <button
              key={piece.type}
              ref={el => buttonRefs.current[index] = el}
              className={`promotion-dialog__piece-button ${
                selectedIndex === index ? 'promotion-dialog__piece-button--selected' : ''
              }`}
              onClick={() => handleSelect(piece.type)}
              onMouseEnter={() => setSelectedIndex(index)}
              aria-label={`Promote to ${piece.label}`}
              tabIndex={0}
            >
              <span className="promotion-dialog__piece-symbol">
                {renderPiece(piece.type)}
              </span>
              <span className="promotion-dialog__piece-label">
                {piece.label}
              </span>
            </button>
          ))}
        </div>

        <div className="promotion-dialog__hint">
          Use arrow keys or numbers (1-4) to select, Enter to confirm
        </div>
      </div>
    </div>
  );
};

export default PromotionDialog;
